// src/components/pages/Lab/LabComponent.tsx

import React, { useState, useCallback } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import InputField from '@components/common/InputField/InputField.component'
import DropdownFilter from '@components/common/DropdownFilter/DropdownFilter.component'
import Button from '@components/common/Button/Button.component'
import LabTracker from './LabTracker'
import LabAnalytics from './LabAnalytics'
import AddLabOrder from '@components/layout/Modals/AddLabOrder/AddLabOrderModal'
import LaboratoryList from '@components/layout/Modals/LaboratoryList/LaboratoryList.component'
import './Lab.css'

type TabType = 'tracker' | 'analytics'

const LabComponent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('tracker')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [filters, setFilters] = useState<Record<string, string[]>>({})
  const [showAddModal, setShowAddModal] = useState<boolean>(false)
  const [showLabSettingsModal, setShowLabSettingsModal] =
    useState<boolean>(false)

  const handleChangeTab = useCallback((tab: string): void => {
    if (tab === 'tracker' || tab === 'analytics') {
      setActiveTab(tab as TabType)
    }
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value)
  }

  const handleFilterChange = (newFilters: Record<string, string[]>): void => {
    setFilters(newFilters)
  }

  const handleAddOrder = (): void => {
    setShowAddModal(true)
  }

  const handleCloseAddModal = (): void => {
    setShowAddModal(false)
  }

  const handleCloseLabSettingsModal = (): void => {
    setShowLabSettingsModal(false)
  }

  const tabButtons = [
    { label: 'Lab Tracker', value: 'tracker' },
    { label: 'Analytics', value: 'analytics' },
  ]

  return (
    <Container fluid className="lab-page padd-812">
      <Row className="align-items-center justify-content-between mb-3">
        <Col md="auto">
          <ButtonGroup
            buttons={tabButtons}
            activeTab={activeTab}
            onButtonClick={handleChangeTab}
          />
        </Col>

        <Col md="auto" className="d-flex align-items-center gap-2">
          <InputField
            placeholder={`Search ${activeTab === 'tracker' ? 'Orders' : 'Analytics'}...`}
            value={searchQuery}
            onChange={handleSearchChange}
            leftIcon={<i className="ti ti-search" />}
          />

          <DropdownFilter
            filters={filters}
            filterLabels={{ status: 'Status' }}
            filterOptions={{
              status: [
                'Pending',
                'Tx.Review',
                'Preparing',
                'Out For Delivery',
                'Cancelled',
                'Completed',
              ],
            }}
            onFilterChange={handleFilterChange}
            buttonLabel="Filter"
          />

          <Button
            size="md"
            className="button-primary btn-add-order"
            onClick={handleAddOrder}
          >
            Add Order
          </Button>

          <Button
            size="md"
            className="button-secondary btn-lab-settings"
            onClick={() => setShowLabSettingsModal(true)}
          >
            <i className="mgc mgc_settings_3_fill" />
          </Button>
        </Col>
      </Row>

      <Row>
        <Col>
          {activeTab === 'tracker' && (
            <LabTracker searchQuery={searchQuery} filters={filters} />
          )}
          {activeTab === 'analytics' && <LabAnalytics />}
        </Col>
      </Row>

      <AddLabOrder show={showAddModal} onClose={handleCloseAddModal} />

      <LaboratoryList
        show={showLabSettingsModal}
        onClose={handleCloseLabSettingsModal}
      />
    </Container>
  )
}

export default LabComponent
